// Armor Games Interface (AGI) integration.
//
// Two distinct concerns live here:
//
// 1. CREDENTIALS — how the game learns *who* the logged-in Armor Games user is.
//    For an externally-hosted iframe game (this project: backend on Azure, not on
//    a *.armorgames.com origin) Armor Games appends `user_id` and `auth_token` to
//    the iframe `src` as query parameters. We read those first. As a fallback —
//    for the case where the game is served from an Armor Games domain and the
//    classic `document.domain` bridge applies — we also try `window.parent.apiAuth`.
//    The server then validates the pair against the AG REST auth endpoint.
//
// 2. AGI METHODS — the `ArmorGames` JS object (saveGame/retrieveGame/quests…).
//    This object is only reachable when `parent.agi` is exposed, which in practice
//    requires the game to share the armorgames.com document.domain. For an
//    externally-hosted iframe it is usually NOT reachable, so every AGI call here
//    is best-effort and fails silently. See docs:
//    https://developers.armorgames.com/docs/javascript-api/usage/
//    https://developers.armorgames.com/docs/javascript-api/storage-methods/

export interface AgCredentials {
  userId: string;
  authToken: string;
}

interface ArmorGamesInstance {
  saveGame(key: string, data: unknown): Promise<{ key: string }>;
  retrieveGame(key: string): Promise<unknown>;
}

interface ApiAuth {
  user_id?: string;
  auth_token?: string;
  game_id?: string;
}

declare global {
  interface Window {
    // Provided by ag.min.js once loaded.
    ArmorGames?: new (opts: Record<string, unknown>) => ArmorGamesInstance;
    apiAuth?: ApiAuth;
    agi?: unknown;
  }
  // Vite injects build-time env vars on import.meta.env; the project has no
  // vite/client reference, so declare the slice we use here.
  interface ImportMeta {
    readonly env: Record<string, string | undefined>;
  }
}

/**
 * Extracts the Armor Games credentials for this session, or null when the game
 * is not running inside an authenticated Armor Games iframe.
 *
 * Order of precedence:
 *   1. `?user_id=…&auth_token=…` on the current URL (externally-hosted iframe).
 *   2. `window.parent.apiAuth` (same-domain / document.domain bridge).
 */
export function getArmorGamesCredentials(): AgCredentials | null {
  // 1. Query string — the supported path for externally-hosted iframe games.
  try {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get('user_id');
    const authToken = params.get('auth_token');
    if (userId && authToken) {
      return { userId, authToken };
    }
  } catch {
    /* ignore malformed URL */
  }

  // 2. Parent frame — only readable when same-origin / document.domain matches.
  try {
    if (window.parent !== window) {
      const parentAuth = window.parent.apiAuth;
      if (parentAuth?.user_id && parentAuth?.auth_token) {
        return { userId: parentAuth.user_id, authToken: parentAuth.auth_token };
      }
    }
  } catch {
    /* cross-origin access blocked — expected for external hosting */
  }

  return null;
}

// ── AGI object (storage / quests) ────────────────────────────────────────────

const AG_API_KEY = import.meta.env.VITE_ARMORGAMES_API_KEY ?? '';

let agInstance: ArmorGamesInstance | null = null;
let initPromise: Promise<ArmorGamesInstance | null> | null = null;

/**
 * Lazily initialises the AGI object using the documented polling pattern
 * (wait for `parent.agi` to appear, then `new ArmorGames({...})`). Resolves to
 * null if the AGI is unreachable — e.g. an externally-hosted iframe where the
 * parent frame is cross-origin. Result is memoised: callers can await freely.
 */
export function initAgi(): Promise<ArmorGamesInstance | null> {
  if (agInstance) return Promise.resolve(agInstance);
  if (initPromise) return initPromise;

  initPromise = new Promise<ArmorGamesInstance | null>((resolve) => {
    // Not in an iframe → nothing to initialise.
    if (window.parent === window) {
      resolve(null);
      return;
    }

    let checks = 0;
    const maxChecks = 40; // ~10s at 250ms

    const tryInit = () => {
      if (checks++ > maxChecks) {
        resolve(null);
        return;
      }
      try {
        const parent = window.parent;
        if (typeof parent.agi !== 'undefined' && typeof window.ArmorGames === 'function') {
          agInstance = new window.ArmorGames({
            user_id: parent.apiAuth?.user_id,
            auth_token: parent.apiAuth?.auth_token,
            game_id: parent.apiAuth?.game_id,
            api_key: AG_API_KEY,
            agi: parent.agi,
          });
          resolve(agInstance);
          return;
        }
      } catch {
        /* cross-origin / not ready yet — keep polling */
      }
      window.setTimeout(tryInit, 250);
    };

    tryInit();
  });

  return initPromise;
}

/**
 * Pushes the player's current standing to Armor Games via `saveGame`.
 *
 * IMPORTANT: Armor Games has no public on-site high-score board API for HTML5
 * games (the scoring/achievement REST endpoints require a proprietary signature
 * computed only inside the AGI). `saveGame` is the supported way to persist a
 * player's score on the Armor Games side, so this keeps the AG-stored score in
 * sync with our own leaderboard's ranking metric. Best-effort: no-op if the AGI
 * is unreachable.
 */
export async function syncScoreToArmorGames(score: {
  reputation: number;
  level: number;
  username: string;
}): Promise<void> {
  try {
    const ag = await initAgi();
    if (!ag) return;
    await ag.saveGame('leaderboard', {
      // `score` is the ranking metric — keep it aligned with the server's
      // GetLeaderboardAsync ordering (currently reputation desc).
      score: score.reputation,
      reputation: score.reputation,
      level: score.level,
      username: score.username,
    });
  } catch {
    /* best-effort — never let AGI failures affect gameplay */
  }
}
